import type { TestCase, TestResult } from '../lib/protocol';
import { createTest } from '../lib/test-cases';

interface Props {
	tests: TestCase[];
	results: Record<string, TestResult>;
	busy: boolean;
	onChange: (tests: TestCase[]) => void;
	onRun: (tests: TestCase[]) => void;
	onDebug: (test: TestCase) => void;
	debugSupported: boolean;
	onUseInput: (input: string) => void;
}

export function TestsPanel({
	tests,
	results,
	busy,
	onChange,
	onRun,
	onDebug,
	debugSupported,
	onUseInput,
}: Props) {
	const update = (id: string, patch: Partial<TestCase>) =>
		onChange(tests.map((test) => (test.id === id ? { ...test, ...patch } : test)));
	return (
		<div className="tests-panel">
			<div className="tests-toolbar">
				<p>Each test starts with fresh program memory.</p>
				<button
					type="button"
					disabled={busy || tests.length >= 50}
					onClick={() => onChange([...tests, createTest()])}
				>
					+ Add test
				</button>
				<button
					type="button"
					className="primary"
					disabled={busy || !tests.length}
					onClick={() => onRun(tests)}
				>
					Run all tests
				</button>
			</div>
			{!tests.length && (
				<p className="muted">Add an input and the exact output your program should produce.</p>
			)}
			{tests.map((test, index) => {
				const result = results[test.id];
				return (
					<details className="test-case" key={test.id} open={tests.length === 1 || undefined}>
						<summary>
							<span
								className={`test-status ${result ? (result.passed ? 'passed' : 'failed') : ''}`}
							>
								{result ? (result.passed ? 'PASS' : 'FAIL') : String(index + 1).padStart(2, '0')}
							</span>
							<strong>{test.name || 'Untitled test'}</strong>
							<span className="muted">
								{result
									? `${result.run.status} · ${Math.round(result.run.durationMs)} ms`
									: 'Not run'}
							</span>
						</summary>
						<fieldset disabled={busy} className="test-fields">
							<label>
								Name
								<input
									value={test.name}
									maxLength={100}
									onChange={(e) => update(test.id, { name: e.target.value })}
								/>
							</label>
							<div className="two-columns">
								<label>
									stdin
									<textarea
										value={test.stdin}
										onChange={(e) => update(test.id, { stdin: e.target.value })}
										spellCheck={false}
									/>
								</label>
								<label>
									Expected stdout
									<textarea
										value={test.expectedStdout}
										onChange={(e) => update(test.id, { expectedStdout: e.target.value })}
										spellCheck={false}
									/>
								</label>
							</div>
							<div className="test-options">
								<label>
									Exit code
									<input
										type="number"
										min={0}
										max={255}
										value={test.expectedExitCode}
										onChange={(e) =>
											update(test.id, {
												expectedExitCode: Math.min(
													255,
													Math.max(0, Math.trunc(Number(e.target.value))),
												),
											})
										}
									/>
								</label>
								<label>
									Timeout (ms)
									<input
										type="number"
										min={100}
										max={30000}
										step={100}
										value={test.timeoutMs}
										onChange={(e) =>
											update(test.id, {
												timeoutMs: Math.min(30000, Math.max(100, Number(e.target.value))),
											})
										}
									/>
								</label>
								<label className="checkbox-label">
									<input
										type="checkbox"
										checked={test.normalizeWhitespace}
										onChange={(e) => update(test.id, { normalizeWhitespace: e.target.checked })}
									/>
									Normalize whitespace
								</label>
							</div>
							<div className="test-actions">
								<button type="button" onClick={() => onRun([test])}>
									Run this test
								</button>
								<button type="button" disabled={!debugSupported} onClick={() => onDebug(test)}>
									Debug this test
								</button>
								<button type="button" onClick={() => onUseInput(test.stdin)}>
									Use input in console
								</button>
								<button
									type="button"
									className="danger-text"
									onClick={() => onChange(tests.filter((t) => t.id !== test.id))}
								>
									Remove
								</button>
							</div>
						</fieldset>
						{result && (
							<div className="test-result">
								<p className={result.passed ? 'success-text' : 'danger-text'}>
									{result.passed
										? 'Output and exit code match.'
										: (result.run.error ??
											`Output ${result.outputMatches ? 'matches' : 'differs'}; exit code ${result.run.exitCode ?? 'unavailable'} (expected ${test.expectedExitCode}).`)}
								</p>
								{!result.outputMatches && (
									<div className="two-columns output-diff">
										<div>
											<h4>Expected · escaped</h4>
											<pre>{JSON.stringify(test.expectedStdout)}</pre>
										</div>
										<div>
											<h4>Actual · escaped</h4>
											<pre>{JSON.stringify(result.run.stdout)}</pre>
										</div>
									</div>
								)}
								{result.run.stderr && <pre className="stderr">{result.run.stderr}</pre>}
							</div>
						)}
					</details>
				);
			})}
		</div>
	);
}
